/**
 * 商户门户 API 错误语义映射（统一入口，全门户失败提示完善）。
 *
 * 背景：按 code 给可行动中文，避免失败 toast 直透后端 message（英文/内部语义）。
 * 本模块是唯一映射入口：
 *
 * - 通用层：鉴权 / 参数 / 限频 / 并发 / 服务不可用 / 无 code 的网络层错误分类
 * - 领域层：按 merchant 前端实际消费端点域（见 src/api/*.ts）从 allinks-common
 *   ErrorCode.java（唯一码表真源，只读）摘录，每条「原因 + 下一步」
 * - 卡域：数据源在 cardApiErrors.ts 的 CARD_API_ERROR_MESSAGES；调用方显式传
 *   `{ domain: 'card' }` 时先查卡域表，再回退领域/通用表（B3 单轨）
 *
 * 码位复用陷阱（重要，改动前必读）：
 * - 422 家族已拆码：CARD_REVEAL_422 / MFA_422 / CARD_LIVENESS_422（码值即语义），
 *   卡域 reveal 场景仅收录 CARD_REVEAL_422（走 domain='card'），通用层不重复；
 * - 'AUTH_403' 卡域文案含「确认卡片归属」，不得全局化：无 domain 时走通用中性
 *   文案，domain='card' 时优先卡域文案；
 * - '4003' 划转复核状态 vs 费项被商户覆盖引用复用，取折中语义；
 * - '42901'（reveal 限速 / OpenAPI 限流）与 '1008'（互斥写 / 乐观锁）均为同义
 *   复用，直接给统一文案。
 *
 * 未收录 code → fallback 原文（通常传后端 message）；code === undefined → 按
 * client.ts 网络层语义分类（e.message / 'network_error' / 'HTTP xxx' / 'invalid_json'）。
 * 纯函数、零 React 依赖，供全部页面与 node 直跑单测共用。
 */
import { CARD_API_ERROR_MESSAGES } from './cardApiErrors.ts';

/** 通用层：跨域共现的鉴权 / 校验 / 限频 / 并发 / 可用性码 */
const GENERAL_MESSAGES: Record<string, string> = {
  // ---- 鉴权（401 家族；AUTH_401/AUTH_401_X 正常被 client.ts refresh 吞掉，留兜底）----
  AUTH_401: '登录状态已失效，请重新登录',
  AUTH_401_X: '登录已过期，请重新登录',
  AUTH_401_C: '账号或密码错误，请重新输入',
  AUTH_401_T: '登录凭证已失效，请重新登录',
  AUTH_401_CH: '登录凭证与当前门户不匹配，请从商户后台入口登录',
  AUTH_401_D: '账号已被禁用，请联系平台处理',
  AUTH_403: '无权执行该操作，请确认操作对象归属后重试',
  // ---- 校验 / 资源 ----
  VAL_400: '请求参数有误，请检查填写内容后重试',
  NOT_FOUND: '请求的资源不存在或已下线',
  // ---- 限频 / 并发 / 可用性 ----
  '42901': '操作过于频繁，请稍后再试',
  RATE_LIMIT: '操作过于频繁，请稍后再试',
  '1008': '请求正在处理中或数据已被他人变更，请刷新后重试',
  '1009': '重复请求的参数不一致，请刷新页面后重试',
  CONC_409: '数据已被他人修改，请刷新后重试',
  SVC_503: '服务暂时不可用，请稍后重试',
  SVC_503_STUB: '服务暂时不可用，请稍后重试',
  // ---- 引导（client.ts 对 3417 已自动跳 KYB 页，此为兜底文案）----
  '3417': '开户资料待完善，请先完成开户资料提交',
};

/** 领域层：merchant 前端实际消费端点对应的业务码（真源 ErrorCode.java 摘录） */
const DOMAIN_MESSAGES: Record<string, string> = {
  // ---- 卡片 / 开卡 / 产品（/cards、/card-issue-jobs、/cards/batch/transfer 等）----
  CARD_409: '卡片当前状态不允许该操作，请刷新后重试',
  CARD_402: '卡内余额不足，请先充值或调低金额',
  CARD_407: '该卡无欠缴，无需补扣',
  JOB_404: '批量开卡任务不存在或已被删除，请刷新任务列表',
  CARD_3302: '该持卡人开卡张数已达上限，无法继续开卡',
  CARD_4303: '系统卡片短码资源已耗尽，请联系平台处理',
  CHN_502: '发卡通道暂时不可用，请稍后重试',
  CHN_409: '发卡通道拒绝了该操作，请核对信息后重试或联系平台',
  PROD_404: '卡产品不存在或已下架，请刷新后重新选择',
  PROD_409: '卡产品编码已存在，请更换编码',

  // ---- KYC（/kyc/cases、/kyc/self-review、/kyc/batch-import、liveness）----
  '3301': '该用户尚未完成身份认证（KYC），请先引导完成认证',
  KYC_404: 'KYC 申请不存在或已被处理，请刷新列表',
  '3302': 'KYC 补件次数已达上限（3 次），请重新发起申请',
  KYC_409: '当前 KYC 状态不允许该操作，请刷新后重试',
  KYC_403: '活体认证须持卡人本人完成，商户不可代填',
  '3420': '存在未缴清的 KYC 巡检罚款，请先在「KYC 罚款」页结清后再开卡',
  '3303': '该用户已通过活体认证，无需重复认证',
  '4019': '活体链接已过期，请重新生成后发送给持卡人',

  // ---- 商户 / 开户（/settings/account、/onboarding-application）----
  MCH_404: '商户不存在或已被注销',
  MCH_405: '证照资料不存在，请先上传证照',
  MCH_406: '证照类型不合法，请重新选择',
  MCH_409: '当前商户状态不允许该操作，如有疑问请联系平台',
  ONB_404: '开户申请不存在或已被处理，请刷新后重试',
  '3201': '开户必填资料缺失，请补全必填项后提交',
  '3202': '今日开户提交次数已达上限，请明日再试',
  '3203': '该注册号/执照号已存在有效商户，请勿重复注册',
  '3205': '该法人 90 天内禁止再次申请开户',
  '3206': '所属代理商状态暂不可提交开户申请，请联系代理商',
  '3207': '开户补件次数已达上限，请联系平台人工处理',
  '3211': '邀请链接已失效，请联系代理商重新获取',

  // ---- 头寸 / 充值 / 提现（/positions、/position-topups、/position-returns、/user-deposits、/withdrawals）----
  POS_404: '头寸/充值/调回记录不存在或不属于本商户，请刷新后重试',
  POS_409: '头寸记录当前状态不允许该操作（回调金额不一致时请联系平台核对）',
  '3401': '可调回额度不足，请调低金额或先补充头寸',
  '3402': '当前商户状态不允许调回，如有疑问请联系平台',
  '3404': '该调回单当前状态不允许撤销，请刷新后重试',
  '3405': '金额低于最低充值/调回限额（100 USD），请调整金额',
  '3408': '头寸账户状态异常（冻结/关闭），请联系平台处理',
  RCFG_404: '充值配置不存在，请刷新后重试',
  RCFG_409: '该产品的充值配置已存在，请直接编辑既有配置',
  DEP_404: '充值单不存在或不属于本商户',
  DEP_409: '充值单当前状态不允许该操作，请刷新后重试',
  ADDR_404: '充值地址尚未配置，请联系平台',
  WDR_404: '提现单不存在或不属于本商户',
  WDR_409: '提现单当前状态不允许该操作，请刷新后重试',
  // 收款账户（/payout-accounts、/withdrawals）：后端文案为「代理」口径，商户口径在此覆盖
  PYM_404: '收款账户不存在，请刷新收款账户列表后重新选择',
  PYM_403: '该收款账户不属于当前商户，请刷新后重新选择本人账户',
  PYM_409: '该收款账户未通过验证或已停用，请更换账户或联系平台完成验资',
  '3005': '存在回冲欠款暂不可提现，请先结清欠款',

  // ---- 划转 / 佣金（/merchant-credits）----
  CMC_404: '佣金账户或分录不存在，请刷新后重试',
  CMC_402: '佣金可用余额不足，请先补充佣金余额',
  '3406': '可划转额度不足，请调低划转金额',
  '3407': '目标持卡人状态不允许收款（未认证/冻结），请先完成认证或更换持卡人',
  MCC_404: '划转单不存在或不属于本商户',
  '4003': '操作对象当前状态不允许该操作（或存在关联引用），请刷新确认后重试',

  // ---- Webhook（/webhooks、redeliver）----
  WH_400: 'Webhook 配置不合法：请检查 URL（须 HTTPS）、事件类型与数量上限',
  WH_403: '无权订阅该事件类型',
  WH_404: 'Webhook 端点或投递记录不存在或不属于本商户',
  WH_409: '该投递当前状态不可重发（仅失败/中止状态可重发）',

  // ---- 邀请 / 注册邮箱（/cardholders/invites、/settings/members/invites、/auth/email/*）----
  INVITE_INVALID: '邀请链接无效，请向邀请方重新获取',
  INVITE_EXPIRED: '邀请链接已过期，请向邀请方重新获取',
  INVITE_REVOKED: '邀请链接已被撤销，请向邀请方重新获取',
  INVITE_EXHAUSTED: '邀请名额已满，请联系邀请方扩容',
  REGISTER_INVITE_REQUIRED: '请使用邀请链接完成注册',
  EMAIL_ALREADY_REGISTERED: '该邮箱已注册或无法使用，请更换邮箱',
  EMAIL_NOT_VERIFIED: '请先验证邮箱后再操作',
  EMAIL_OTP_INVALID: '邮箱验证码错误或已失效，请重新输入',
  EMAIL_OTP_LOCKED: '验证码尝试次数过多，请稍后重试',
  EMAIL_OTP_COOLDOWN: '验证码发送过于频繁，请稍后再试',
  EMAIL_OTP_DAILY_LIMIT: '今日验证码发送已达上限，请明日再试',
  EMAIL_ALREADY_VERIFIED: '邮箱已验证，无需重复操作',
  EMAIL_MISSING: '登录邮箱未配置，请先在账户设置中绑定邮箱',

  // ---- 企业用户域（/corps/*、/users/corporates/*）----
  '4501': '企业对象不存在或已被处理，请刷新后重试',
  '4502': '企业当前状态不允许该操作，请刷新后重试',
  '4503': '资料缺件或成员不存在，请补全后重试',
  '4504': '部门层级超过上限（5 级），请调整层级结构',
  '4505': '该成员已存在，请勿重复添加',
  '4506': '预算不存在或已暂停，请先启用预算',
  '4507': '超出总预算剩余或无预算归属，请调整预算配置',
  '4508': '审批单不存在或已被处理，请刷新后重试',
  '4509': '审批单已在处理或已终审，请刷新后重试',
  '4510': '账单周期重复或报销单状态不允许该操作，请刷新后重试',
  '4511': '该发票或交易已关联报销，请勿重复关联',
  '4512': '企业规则不可宽于上级限制，请收紧后重试',
  '4513': '模板不存在或不可套用，请重新选择',
  '4514': '平台备案上报失败，请稍后重试',
  '4515': '开卡约束不满足（风控/预算上限），请调整后重试',
  '4516': '重复绑定或同类型同作用域配置已存在，请勿重复配置',
  '4517': '高风险行业须转平台审核，不可直接通过',
  '4518': '法人或经办人 KYC 未通过，不可通过该审批',
  '4519': '离职成员存在资金余额，请先处理余额再回收',
  '4550': '企业授权网关暂不可用，请稍后重试',

  // ---- 费项 / 费率（/fee-items、/rates）----
  FEE_404: '费项或费率覆盖不存在/未启用，请刷新后重试',
  FEE_400: '售价不得低于成本价，请调整售价',
  FEE_402: '二次计费扣款失败（余额不足），请先补充余额',
  '1001': '新建费项必须挂接产品，请先选择产品',
  FEE_409: '同维度费项已存在生效行，请编辑既有行或先停用',
  FEE_423: '费项变更冷却中，请冷却期结束后再操作',
  '3101': '费率低于下层成本价，请上调费率',
  '3102': '费率超过平台上限，请下调费率',
  '3103': '费率变更冷却中，请稍后再试',

  // ---- 账务 / 发票（/finance/invoices、/finance/settlement-entries）----
  INV_422: '开票金额超过可开票额度，请调整金额',
  INV_403: '专票申请缺少一般纳税人资质，请先上传资质文件',
  STMT_404: '对账单不存在或无权查看',

  // ---- 安全 / MFA / 代登录（/settings/security/*、/auth/portal-login）----
  '3410': '2FA 验证码缺失或错误，请重新输入',
  '3412': '请先启用两步验证（2FA）再执行该操作',
  '3413': '2FA 绑定会话已过期，请重新发起绑定',
  '3414': '已启用 2FA，请先解绑后再重新绑定',
  PASSWORD_REQUIRED: '请输入密码',
  PASSWORD_INCORRECT: '密码错误，请重新输入',
  '3415': '安全服务暂时不可用，请稍后重试',
  '3416': '请先修改登录密码后再执行该操作',
  '3428': '代登录票据无效，请回到运营后台重新发起',
  '3429': '代登录票据已过期，请回到运营后台重新发起',
  '3430': '代登录票据已被使用，请回到运营后台重新发起',
  '3432': '代登录会话不允许执行该操作，请退出代登录后使用正式账号',

  // ---- 品牌 / 邮件通道（/settings/brand、/settings/email-channels、/email-templates）----
  BRAND_404: '品牌配置不存在，请先保存配置再发布',
  MAIL_CH_404: '邮件通道不存在，请刷新后重试',
  MAIL_CH_422: '邮件通道测试失败，配置未保存，请核对凭证',

  // ---- 风控中心（/risk/rules、/risk/users、/risk/quick-actions/*）----
  '6001': '风控规则不存在或已被处理，请刷新后重试',
  '6002': '规则编码已存在，请更换编码',
  '6003': '平台基线规则参数不可修改',
  '6004': '规则状态不允许该转换，请刷新后重试',
  '6005': '规则未通过预检，不可直接上线',
  '6006': '高危操作需要二次确认，请确认后重试',
  '6007': '缺少 2FA 验证码，请输入验证码后重试',
  '6009': '名单条目已存在，本次已跳过重复添加',
  '6011': '拦截事件当前状态不允许该处置，请刷新后重试',
  '6012': '平台基线规则不可覆盖',
  '6013': '回滚目标版本不存在，请刷新后重试',
  '6014': '拦截事件不存在或不属于本商户',
  '6015': '覆盖阈值格式不合法，请检查后重试',
  '6016': '批量处置目标数超过单次上限（50），请分批操作',
  '6017': '处置目标不属于本商户，请刷新后重试',
  '6018': '用户风险状态不允许该转换，请刷新后重试',
  '6019': '覆盖项已达 50 条上限，请先停用部分覆盖',

  // ---- 持卡人（/cardholders、/users/individuals）----
  CH_404: '持卡人不存在或已被删除',
  CH_409: '持卡人当前状态不允许该操作，请刷新后重试',
  CH_423: '持卡人已被冻结，禁止相关交易操作',
  CH_413: '批量操作数量超过上限（50），请分批操作',

  // ---- 通知 / 文件（/notices、/files、/cards/export）----
  NTF_404: '通知不存在或已被删除',
  FILE_404: '文件不存在或已超过 7 天有效期，请重新导出',
  EXPORT_502: '导出邮件发送失败，请稍后重试或重新导出',
};

/**
 * 无 code（网络层/HTTP 层）错误的 fallback 分类（对齐 client.ts 语义）：
 * - fetch 抛异常 / 'network_error' → 网络连接异常
 * - 'HTTP <n>'（非 JSON 错误体）→ 按 n 给服务侧文案
 * - 'invalid_json'（2xx 但解析失败）→ 响应格式异常
 */
function classifyNetworkError(fallback: string): string {
  if (fallback === 'network_error' || /failed to fetch|networkerror|load failed|err_(name)?_not_resolved|timed?\s?out|timeout/i.test(fallback)) {
    return '网络连接异常，请检查网络后重试';
  }
  const httpMatch = /^HTTP (\d{3})$/.exec(fallback);
  if (httpMatch) {
    const status = Number(httpMatch[1]);
    if (status >= 500) {
      return `服务暂时不可用（HTTP ${status}），请稍后重试`;
    }
    if (status === 404) {
      return '请求的资源不存在（HTTP 404），请刷新后重试';
    }
    if (status === 401 || status === 403) {
      return '登录状态或权限异常，请重新登录后再试';
    }
    return `请求失败（HTTP ${status}），请稍后重试`;
  }
  if (fallback === 'invalid_json') {
    return '服务响应格式异常，请稍后重试';
  }
  return fallback;
}

/** 错误域提示：卡域调用方显式声明，卡域专用文案优先 */
type ApiErrorDomain = 'card';

/**
 * 按后端业务码映射用户可读提示（全门户唯一入口）。
 *
 * @param code 后端信封业务码（client.ts ApiResult.code；网络层错误为 undefined）
 * @param fallback 未收录 code 的回退文案（通常传后端 message 原文）
 * @param options.domain 传 'card' 时先查卡域表，再回退领域/通用表；
 *   省略时保持既有行为（领域 → 通用 → 卡域兜底，但 AUTH_403 不走卡域文案）
 */
export function mapApiError(
  code: string | undefined,
  fallback: string,
  options?: { domain?: ApiErrorDomain },
): string {
  if (code === undefined) {
    return classifyNetworkError(fallback);
  }
  if (options?.domain === 'card') {
    const cardHit = CARD_API_ERROR_MESSAGES[code];
    if (cardHit) {
      return cardHit;
    }
    return DOMAIN_MESSAGES[code] ?? GENERAL_MESSAGES[code] ?? fallback;
  }
  const hit = DOMAIN_MESSAGES[code] ?? GENERAL_MESSAGES[code];
  if (hit) {
    return hit;
  }
  // 无 domain 的既有语义：卡域表仅作兜底；AUTH_403 保持通用中性文案
  if (code !== 'AUTH_403') {
    const cardHit = CARD_API_ERROR_MESSAGES[code];
    if (cardHit) {
      return cardHit;
    }
  }
  return fallback;
}

/**
 * catch 分支兜底：意外异常（含 api 层抛出的业务失败 Error）转用户可读文案。
 * api 层 throw 携带 code 属性时走业务码映射；网络类英文异常消息
 * （Failed to fetch 等）归类为中文；其余技术消息不裸透给用户。
 */
export function mapThrownError(error: unknown, fallback?: string): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    const message = (error as { message?: unknown }).message;
    if (typeof code === 'string') {
      return mapApiError(code, typeof message === 'string' && message ? message : (fallback ?? ''));
    }
  }
  const message = error instanceof Error ? error.message : '';
  const classified = classifyNetworkError(message);
  if (message && classified !== message) {
    return classified;
  }
  return fallback ?? '操作失败，请稍后重试';
}

/**
 * 仅供测试/守护脚本检视收录面（通用 + 领域 + 卡域并集）。
 * 生产页面请用 mapApiError，不得直接查表。
 */
export function apiErrorCodeKeys(): readonly string[] {
  return Object.freeze([
    ...new Set([
      ...Object.keys(GENERAL_MESSAGES),
      ...Object.keys(DOMAIN_MESSAGES),
      ...Object.keys(CARD_API_ERROR_MESSAGES),
    ]),
  ]);
}
